<template>
  <div v-if="!isMobile" @click="show()" class="flex">
    <div class="course course-teaser course-product mb-8">
      <div class="course-thumbnail" :style="'background-image: url(' + product.images.medium + ');'" alt="" />

      <div v-if="product.course?.badges" class="course-badges">
        <div v-for="badge in product.course.badges" :key="badge" class="course-badge">
          <img :src="'/img/badges/' + badge + '.svg'" alt="Badge" />
        </div>
      </div>

      <div
        v-if="
          hasDiscountLabel(
            product.price.default,
            product.price.original,
            product.price.membership,
            isMembershipCustomer,
          )
        "
        class="absolute top-8 -left-2 mdl-color--warning px-6 py-1 rounded font-bold flex flex-col"
      >
        <span>
          {{ getDiscountLabel(product.price.default, product.price.original) }}
        </span>
      </div>

      <div class="course-content">
        <h2 class="course-title max-h-fit">
          {{ product.title }}
          <small>
            {{ product.identifier }}
          </small>
        </h2>
        <div v-if="nextSession" class="msg msg--success my-4">
          <strong>{{ trans('Next session') }}</strong>
          <div>
            {{ nextSession.from.dateName }} &mdash;
            {{ transChoice(':num seats left', nextSession.seats.available) }}
          </div>
        </div>
      </div>

      <footer class="course-footer">
        <div class="flex justify-between items-end">
          <ProductPrice
            :price="product.price.default"
            :original="product.price.original"
            :member-price="product.price.membership"
            :membership="isMembershipCustomer"
            :membership-enabled="shop.enableMembership"
          />

          <div @click.stop>
            <MdlButton
              v-if="!product.course.isIlt"
              class="w-[36px] h-[36px]"
              primary
              raised
              :data-qa-id="pendo(`buyOneProduct.${product.id}`)"
              @click="addToCart"
            >
              <Icon name="shopping_cart" class="m-0" />
            </MdlButton>
          </div>
        </div>
      </footer>
    </div>
  </div>

  <div v-else @click="show()">
    <div class="course flex w-full min-h-40 h-fit">
      <div class="course-thumbnail p-0 w-1/3" :style="'background-image: url(' + product.images.medium + ');'" alt="" />
      <div v-if="product.course?.badges" class="course-badges left-0 top-0">
        <div v-for="badge in product.course.badges" :key="badge" class="course-badge">
          <img :src="'/img/badges/' + badge + '.svg'" alt="Badge" />
        </div>

        <div
          v-if="
            hasDiscountLabel(
              product.price.default,
              product.price.original,
              product.price.membership,
              isMembershipCustomer,
            )
          "
          class="absolute top-4 -left-2 mdl-color--warning px-2 py-1 rounded font-bold"
        >
          <span class="text-sm">
            {{ getDiscountLabel(product.price.default, product.price.original) }}
          </span>
        </div>
      </div>

      <div class="course-content flex flex-col gap-y-4 justify-between w-2/3">
        <div>
          <h4 class="course-title text-md flex flex-col break-words">
            {{ product.title }}
            <small>
              {{ product.identifier }}
            </small>
          </h4>
          <div v-if="nextSession" class="msg msg--success mt-4">
            <strong>{{ trans('Next session') }}</strong>
            <div>
              {{ nextSession.from.dateName }} &mdash;
              {{ transChoice(':num seats left', nextSession.seats.available) }}
            </div>
          </div>
        </div>

        <footer class="flex justify-between items-end">
          <ProductPrice
            :price="product.price.default"
            :original="product.price.original"
            :member-price="product.price.membership"
            :membership="isMembershipCustomer"
            :membership-enabled="shop.enableMembership"
          />
          <div @click.stop>
            <MdlButton
              v-if="!product.course.isIlt"
              class="w-[36px] h-[36px]"
              primary
              raised
              :data-qa-id="pendo(`buyOneProduct.${product.id}`)"
              @click="addToCart"
            >
              <Icon name="shopping_cart" class="m-0" />
            </MdlButton>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, Ref } from 'vue'
import { pendoLabel as pendo } from '@/munio/utils'

import Icon from '@component/Icon.vue'
import MdlButton from '@component/mdl/Button.vue'
import ProductPrice from '@component/ShopProduct/ProductPrice.vue'

import { trans, transChoice } from '@/munio/i18n/index.js'
import { ShopProductResourceDto, ShopResourceDto, Price } from '@/munio/api/data'
import { useDiscount } from '@composable/useDiscount'

const props = defineProps<{
  product: ShopProductResourceDto
  shop: ShopResourceDto
  isMembershipCustomer: boolean
  isMobile: boolean
}>()

const emit = defineEmits<{
  (e: 'show'): void
}>()

const { Currency } = window.Munio

const nextSession = computed(() =>
  props.product.course?.isIlt && props.product?.sessions[0] ? props.product?.sessions[0] : null,
)

function show() {
  emit('show')
}

async function addToCart(e: Event) {
  try {
    await window.Munio.api.shop().cart.add({
      productId: props.product.id,
      quantity: 1,
    })
  } catch (err) {
    console.error(err)
  }
}

function hasDiscountLabel(defaultP: Price, fullP: Price, membershipP: Price | undefined, membership: boolean): boolean {
  if ((!membership || !membershipP) && defaultP && fullP && defaultP.value < fullP.value) {
    return true
  }

  if (
    membership &&
    defaultP &&
    fullP &&
    membershipP &&
    defaultP.value < fullP.value &&
    defaultP.value < membershipP.value
  ) {
    return true
  }

  return false
}

function getDiscountLabel(defaultP: Price, fullP: Price) {
  if (defaultP && fullP) {
    const { discount, percent } = useDiscount(defaultP.value, fullP.value)

    if (percent.value % 5 === 0) {
      return trans('Save :amount', { amount: percent.value + '%' })
    } else {
      return trans('Save :amount', { amount: Currency.format(discount.value, props.shop.currency) })
    }
  }

  return null
}
</script>
